// @ai-generated 2026-07-21T03:45:01+00:00 by philipp.kammerer@synqony.com using claude-fable-5
import { useCallback, useMemo, useState } from 'react';
import { useLocation } from 'react-router';
import {
    useNotificationSettingsQuery,
    useResetNotificationSettings,
    useUpsertNotificationSetting,
} from '@Hooks/Messages';
import { useMainMenuElements } from '@Hooks/Navigation/useMainMenuElements';
import { isMenuGroup, type MenuElement } from '@Hooks/Navigation/types';
import { EnumNotificationModule } from '@Model';
import {
    FlexColumn,
    FlexRow,
    ScrollContainer,
    Separator,
    Switch,
    useTranslate,
} from '@synqo/frontend-component-library';
import { IconArrowLeft, IconChevronDown, IconChevronRight, IconMapPin } from '@tabler/icons-react';
import {
    EMAIL_TEMPLATE_TYPE_KEYS,
    getCatalogForPartnershipType,
    isMandatoryInApp,
    isModuleEmailAvailable,
    isModuleMandatoryInApp,
    resolveChannels,
    resolveModuleChannels,
    type NotificationCatalogModule,
} from './notificationCatalog';
import { useSession } from '@Contexts/session/SessionContext';
import styles from './NotificationHubButton.module.css';

interface NotificationSettingsPanelProps {
    onBack: () => void;
    /** Pins this module's settings on top instead of deriving the module from the current route
     * (used when the panel is opened from a specific notification's row menu). */
    pinnedModule?: EnumNotificationModule;
}

/** Notification module per main-menu group, for groups where every link shares one module. */
const MENU_GROUP_TO_MODULE: Record<string, EnumNotificationModule> = {
    Billing: EnumNotificationModule.Billing,
    Systemverwaltung: EnumNotificationModule.System,
};

/** Per-link overrides for groups whose links span more than one module; checked before MENU_GROUP_TO_MODULE. */
const LINK_TO_MODULE: Record<string, EnumNotificationModule> = {
    '/orders': EnumNotificationModule.Requisitions,
    '/requisitions': EnumNotificationModule.Requisitions,
    '/invoices': EnumNotificationModule.Billing,
};

/** Maps the current route to the notification module the settings panel pins on top;
 * the longest matching menu link wins. */
function getModuleByPathname(pathname: string, menu: MenuElement[]): EnumNotificationModule | undefined {
    // The dynamic-export pages live under /settings/export*, which the main menu does not cover.
    if (pathname.includes('export')) {
        return EnumNotificationModule.Exports;
    }

    const candidates = menu
        .filter(isMenuGroup)
        .flatMap((group) => group.targets.map((target) => ({ link: target.link, groupName: group.name })))
        .filter((candidate): candidate is { link: string; groupName: string } => !!candidate.link)
        .sort((a, b) => b.link.length - a.link.length);

    const match = candidates.find((candidate) => pathname.includes(candidate.link));
    if (!match) {
        return undefined;
    }

    return LINK_TO_MODULE[match.link] ?? MENU_GROUP_TO_MODULE[match.groupName];
}

export function NotificationSettingsPanel({ onBack, pinnedModule }: NotificationSettingsPanelProps) {
    const i18n = useTranslate('NotificationHub');
    const { pathname } = useLocation();
    const { settings } = useNotificationSettingsQuery();
    const { mutateAsync: upsert } = useUpsertNotificationSetting();
    const { mutateAsync: resetAll } = useResetNotificationSettings();
    const [expandedModules, setExpandedModules] = useState<ReadonlySet<EnumNotificationModule>>(new Set());

    const menu = useMainMenuElements();
    const { current } = useSession();
    const catalog = useMemo(() => getCatalogForPartnershipType(current.partnershipType), [current.partnershipType]);
    const currentModule = pinnedModule ?? getModuleByPathname(pathname, menu);
    const [showAllSettings, setShowAllSettings] = useState(currentModule === undefined);
    const pinnedEntry = useMemo(
        () => catalog.find((moduleEntry) => moduleEntry.module === currentModule),
        [catalog, currentModule],
    );
    const otherEntries = useMemo(
        () => catalog.filter((moduleEntry) => moduleEntry.module !== currentModule),
        [catalog, currentModule],
    );

    const setChannel = useCallback(
        (module: EnumNotificationModule, typeKey: string | undefined, channel: 'in_app' | 'email', value: boolean) => {
            const current = resolveChannels(settings, module, typeKey);
            void upsert({
                module,
                key: typeKey,
                in_app: channel === 'in_app' ? value : current.inApp,
                email: channel === 'email' ? value : current.email,
            });

            if (typeKey === undefined) {
                // Type-level overrides would out-vote the new module default, so the channel is written through.
                settings
                    .filter((s) => s.module === module && s.key !== undefined)
                    .forEach((typeOverride) => {
                        void upsert({
                            module,
                            key: typeOverride.key,
                            in_app: channel === 'in_app' ? value : typeOverride.in_app,
                            email: channel === 'email' ? value : typeOverride.email,
                        });
                    });
            }
            return true;
        },
        [settings, upsert],
    );

    const toggleExpanded = useCallback((module: EnumNotificationModule) => {
        setExpandedModules((previous) => {
            const next = new Set(previous);
            if (next.has(module)) {
                next.delete(module);
            } else {
                next.add(module);
            }
            return next;
        });
    }, []);

    const renderChannelSwitches = (
        moduleEntry: NotificationCatalogModule,
        typeKey: string | undefined,
        namePrefix: string,
        emailAvailable: boolean,
    ) => {
        const module = moduleEntry.module;
        const channels =
            typeKey === undefined
                ? resolveModuleChannels(settings, moduleEntry)
                : resolveChannels(settings, module, typeKey);
        return (
            <>
                <Switch
                    name={`${namePrefix}_in_app`}
                    size="sm"
                    value={channels.inApp}
                    readOnly={typeKey === undefined ? isModuleMandatoryInApp(moduleEntry) : isMandatoryInApp(typeKey)}
                    onChange={(value) => setChannel(module, typeKey, 'in_app', value)}
                />
                {emailAvailable ? (
                    <Switch
                        name={`${namePrefix}_email`}
                        size="sm"
                        value={channels.email}
                        onChange={(value) => setChannel(module, typeKey, 'email', value)}
                    />
                ) : (
                    <span />
                )}
            </>
        );
    };

    const renderTypeRows = (moduleEntry: NotificationCatalogModule) =>
        moduleEntry.types.map((typeEntry) => (
            <div key={typeEntry.typeKey} className={styles.settingsRow} data-level="type">
                <span className={styles.settingsRowLabel}>{i18n(typeEntry.labelDe)}</span>
                {renderChannelSwitches(
                    moduleEntry,
                    typeEntry.typeKey,
                    `notification_setting_type_${typeEntry.typeKey}`,
                    EMAIL_TEMPLATE_TYPE_KEYS.has(typeEntry.typeKey),
                )}
            </div>
        ));

    // A module the session cannot see pins nothing, so the full list is the only thing left to show.
    const showAll = showAllSettings || pinnedEntry === undefined;
    const isShortMenu = !showAll;

    return (
        <FlexColumn justify="start" align="stretch" gap="sm" width="392px">
            <FlexRow justify="start" align="center" gap="xs" className={styles.header}>
                <button
                    type="button"
                    aria-label={i18n('Zurück')}
                    onClick={onBack}
                    className={styles.settingsBackButton}
                >
                    <IconArrowLeft size={16} />
                </button>
                <span className={styles.headerTitle}>
                    {isShortMenu
                        ? `${i18n('Benachrichtigungen')}: ${i18n(pinnedEntry.labelDe)}`
                        : i18n('Alle Benachrichtigungen')}
                </span>
            </FlexRow>
            <div className={styles.settingsColumnHeader}>
                <span />
                <span>{i18n('In-App')}</span>
                <span>{i18n('E-Mail')}</span>
            </div>
            <Separator />
            {isShortMenu && (
                <FlexColumn justify="start" align="stretch" gap="none">
                    {renderTypeRows(pinnedEntry)}
                    <button
                        type="button"
                        className={styles.settingsShowAllButton}
                        onClick={() => setShowAllSettings(true)}
                    >
                        {i18n('Alle Einstellungen')} →
                    </button>
                </FlexColumn>
            )}
            {showAll && (
                <ScrollContainer height="380px" scrollbars="y">
                    <FlexColumn justify="start" align="stretch" gap="none">
                        {pinnedEntry && (
                            <>
                                <div className={styles.settingsSectionLabel}>{i18n('Aktuelles Modul')}</div>
                                <div className={styles.settingsRow} data-level="module">
                                    <span className={styles.settingsRowLabel} data-with-icon="">
                                        <IconMapPin size={12} />
                                        {i18n(pinnedEntry.labelDe)}
                                    </span>
                                    {renderChannelSwitches(
                                        pinnedEntry,
                                        undefined,
                                        `notification_setting_module_${pinnedEntry.module}`,
                                        isModuleEmailAvailable(pinnedEntry),
                                    )}
                                </div>
                                {renderTypeRows(pinnedEntry)}
                                <div className={styles.settingsSectionLabel}>{i18n('Weitere Module')}</div>
                            </>
                        )}
                        {otherEntries.map((moduleEntry) => {
                            const isExpanded = expandedModules.has(moduleEntry.module);
                            return (
                                <div key={moduleEntry.module}>
                                    <div className={styles.settingsRow} data-level="module">
                                        <button
                                            type="button"
                                            className={styles.settingsExpandButton}
                                            aria-expanded={isExpanded}
                                            onClick={() => toggleExpanded(moduleEntry.module)}
                                        >
                                            {isExpanded ? (
                                                <IconChevronDown size={14} />
                                            ) : (
                                                <IconChevronRight size={14} />
                                            )}
                                            <span className={styles.settingsRowLabel}>{i18n(moduleEntry.labelDe)}</span>
                                        </button>
                                        {renderChannelSwitches(
                                            moduleEntry,
                                            undefined,
                                            `notification_setting_module_${moduleEntry.module}`,
                                            isModuleEmailAvailable(moduleEntry),
                                        )}
                                    </div>
                                    {isExpanded && renderTypeRows(moduleEntry)}
                                </div>
                            );
                        })}
                    </FlexColumn>
                </ScrollContainer>
            )}
            {showAll && (
                <>
                    <Separator />
                    <FlexRow justify="space-between" align="center" className={styles.header}>
                        <button
                            type="button"
                            className={styles.settingsResetLink}
                            onClick={() => void resetAll(undefined)}
                        >
                            {i18n('Auf Standard zurücksetzen')}
                        </button>
                        <span className={styles.settingsSavedHint}>{i18n('Sofort gespeichert')}</span>
                    </FlexRow>
                </>
            )}
        </FlexColumn>
    );
}
